import { useState } from 'react'

import { EmploymentControllerDTO } from '@builddocs/data-management'
import {
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalHeader,
} from '@builddocs/mui'
import { CloudUpload } from '@mui/icons-material'
import { Typography } from '@mui/material'
import * as XLSX from 'xlsx'

import ProcessStatus, { currentProcess, emptyProcess } from './ProcessStatus'
import {
  createEmploymentInviteHardcoded,
  createPositionHardcoded,
  getEmploymentsHireHardcoded,
  getPositionHardcoded,
  getUserHardcoded,
  loginUserHardcoded,
  responseHireInvitationHardcoded,
} from './requests'

interface ExcelRow {
  username: string
  userPassword: string
  positionName: string
  adminUsername: string
  adminPassword: string
}

export const AddUserPosition = () => {
  const [modalOpened, setModalOpened] = useState(false)
  const [isGenerating, setIsGenerating] = useState<boolean | 'finished'>(false)

  const [progress, setProgress] = useState<string>('')
  const [uploadedFile, setUploadedFile] = useState(null)
  const [excelRows, SetExcelRows] = useState<null | ExcelRow[]>(null)
  const [currentProcess, setCurrentProcess] =
    useState<currentProcess>(emptyProcess)
  const [results, setResults] = useState<ExcelRow[] | null>(null)
  const handleFileSelection = event => {
    const file = event.target.files[0]

    if (file) {
      setUploadedFile(file)
      setProgress('Читаем Excel файл...')
      const reader = new FileReader()

      reader.onload = async e => {
        // @ts-expect-error затычка
        const data = new Uint8Array(e.target.result)
        const workbook = XLSX.read(data, { type: 'array' })
        const sheetName = workbook.SheetNames[0]
        const sheet = workbook.Sheets[sheetName]
        const rows = XLSX.utils.sheet_to_json(sheet, { defval: '' })
        const transformedToObject = rows.map(row => {
          if (!row) return
          return {
            username: row['ЛОГИН ПОЛЬЗАКА'] as string,
            userPassword: row['ПАРОЛЬ ПОЛЬЗАКА'] as string,
            positionName: row['ДОЛЖНОСТЬ'] as string,
            adminUsername: row['АДМИН ЛОГИН'] as string,
            adminPassword: row['АДМИН ПАРОЛЬ'] as string,
          }
        })
        // @ts-expect-error затычка

        SetExcelRows(transformedToObject)
        console.log(transformedToObject)
        setProgress('')
      }
      reader.readAsArrayBuffer(file)
    }
  }
  const startGenerating = async () => {
    if (!excelRows || excelRows.length === 0) {
      console.error('Нет данных для обработки')
      return
    }
    setIsGenerating(true)
    setProgress('Пользователь 0 из 0')

    // Ждем выполнения всех операций
    const finishedGeneration = await Promise.all(
      excelRows?.map(async (row, index) => {
        setProgress(
          `Работа с пользователем ${index + 1} из ${excelRows?.length}`,
        )

        const userSession = await loginUserHardcoded({
          username: row.username,
          password: row.userPassword,
        }).then(r => r.data)

        if (userSession.jwtToken)
          setCurrentProcess(prev => ({ ...prev, loginToUser: true }))
        if (!userSession.jwtToken) {
          setCurrentProcess(prev => ({ ...prev, loginToUser: false }))
          return { ...row, status: 'Не удалось войти в пользователя' }
        }

        const currentUserData = await getUserHardcoded(
          userSession.jwtToken,
        ).then(r => r.data)

        if (currentUserData.userId)
          setCurrentProcess(prev => ({ ...prev, findingUserId: true }))
        if (!currentUserData.userId) {
          setCurrentProcess(prev => ({ ...prev, findingUserId: false }))
          return {
            ...row,
            status: 'Не удалось получить пользовательские данные',
          }
        }

        const adminSession = await loginUserHardcoded({
          username: row.adminUsername,
          password: row.adminPassword,
        }).then(r => r.data)

        if (adminSession.jwtToken)
          setCurrentProcess(prev => ({ ...prev, loginToAdmin: true }))
        if (!adminSession.jwtToken) {
          setCurrentProcess(prev => ({ ...prev, loginToAdmin: false }))
          return { ...row, status: 'Не удалось войти в админа' }
        }

        const organizationPositions = await getPositionHardcoded(
          adminSession.jwtToken,
        )
          .then(r => {
            if (adminSession.jwtToken)
              setCurrentProcess(prev => ({ ...prev, gettingPositions: true }))
            return r.data
          })
          .catch(() => {
            if (adminSession.jwtToken)
              setCurrentProcess(prev => ({ ...prev, gettingPositions: false }))
            return undefined
          })

        if (!organizationPositions) {
          setCurrentProcess(prev => ({ ...prev, gettingPositions: false }))
          return { ...row, status: 'Не удалось получить организации' }
        }

        const hasPositionInOrganization = organizationPositions.find(
          orgPos =>
            orgPos.description.trim().toLowerCase() ===
            row.positionName.trim().toLowerCase(),
        )

        if (hasPositionInOrganization)
          setCurrentProcess(prev => ({ ...prev, creatingPosition: 'skip' }))

        const createdOrganizationPosition = hasPositionInOrganization
          ? hasPositionInOrganization
          : await createPositionHardcoded(
              row.positionName.trim(),
              adminSession.jwtToken,
            ).then(r => r.data)

        if (
          !hasPositionInOrganization &&
          createdOrganizationPosition.positionId
        )
          setCurrentProcess(prev => ({ ...prev, creatingPosition: true }))
        if (!createdOrganizationPosition) {
          setCurrentProcess(prev => ({ ...prev, creatingPosition: false }))
          return {
            ...row,
            status: 'Не удалось создать или получить позицию для пользователя',
          }
        }

        const organizationInvitation = await createEmploymentInviteHardcoded(
          currentUserData.userId,
          '-',
          '2024-01-01',
          {
            description: createdOrganizationPosition.description,
            inn: createdOrganizationPosition.inn,
            positionId: createdOrganizationPosition.positionId,
          },
          adminSession.jwtToken,
        ).then()

        if (organizationInvitation.status === 200)
          setCurrentProcess(prev => ({ ...prev, creatingPosition: true }))
        if (organizationInvitation.status !== 200) {
          setCurrentProcess(prev => ({ ...prev, creatingPosition: false }))
          return { ...row, status: 'Не удалось пригласить в организацию' }
        }
        const dataNotifications = await getEmploymentsHireHardcoded(
          userSession.jwtToken,
        )

        if (!dataNotifications || !dataNotifications.entityList.length) {
          setCurrentProcess(prev => ({ ...prev, acceptingInvitation: false }))
          return { ...row, status: 'Не удалось принять приглашение' }
        }
        const hireAcceptanceResponse = dataNotifications.entityList[0]
        const hireAcceptanceDTO: EmploymentControllerDTO = {
          businessKey: hireAcceptanceResponse.businessKey,
          senderName: hireAcceptanceResponse.senderName,
          receiverName: hireAcceptanceResponse.receiverName,
          senderId: hireAcceptanceResponse.senderId,
          receiverId: hireAcceptanceResponse.receiverId,
          senderEmail: hireAcceptanceResponse.senderEmail,
          receiverEmail: hireAcceptanceResponse.receiverEmail,
          positionName: hireAcceptanceResponse.positionName,
          orderName: hireAcceptanceResponse.orderName,
          orderDate: hireAcceptanceResponse.orderDate,
        }
        const hireAcceptance = await responseHireInvitationHardcoded(
          userSession.jwtToken,
          hireAcceptanceDTO,
        ).then()

        if (hireAcceptance.status === 200)
          setCurrentProcess(prev => ({ ...prev, acceptingInvitation: true }))
        return { ...row, status: 'Успешно' }
      }),
    )

    if (finishedGeneration) {
      console.log(finishedGeneration)
      setResults(finishedGeneration)
      return setIsGenerating('finished')
    }
  }

  const downloadResults = () => {
    if (!results) return

    const sheet = XLSX.utils.json_to_sheet(results)
    const workbook = XLSX.utils.book_new()

    XLSX.utils.book_append_sheet(workbook, sheet, 'Результаты')
    XLSX.writeFile(workbook, 'Результаты.xlsx')
  }

  return (
    <>
      <Button
        style={{ backgroundColor: 'purple', color: 'white' }}
        onClick={() => setModalOpened(true)}
      >
        Добавить пользователей в орги
      </Button>
      <Modal open={modalOpened} onClose={() => setModalOpened(false)}>
        <ModalContent>
          <ModalHeader>
            <Typography>Добавление пользователей в организации</Typography>
          </ModalHeader>

          <ModalBody>
            {/*ФАЙЛ НЕ ЗАГРУЖЕН - МЫ ТУТ*/}
            {!uploadedFile ? (
              <Button
                component="label"
                startIcon={<CloudUpload />}
                variant="contained"
              >
                Загрузить Excel файл
                <input
                  accept=".xlsx, .xls"
                  style={{ display: 'none' }}
                  type="file"
                  onChange={handleFileSelection}
                />
              </Button>
            ) : (
              <></>
            )}
            {/*ФАЙЛ ЗАГРУЖЕН - ИНИЦИАЛИЗИРУЕМ ПРОЦЕСС*/}
            {excelRows?.length && uploadedFile !== null ? (
              isGenerating === false ? (
                <Button onClick={startGenerating}>Начать генерацию</Button>
              ) : (
                <Typography>{progress}</Typography>
              )
            ) : (
              <Typography>Не получилось прочитать файл</Typography>
            )}

            {/*ПРОЦЕСС ИНИЦИАЛИЗИРОВАН - ПОКАЗЫВАЕМ ВСЯКИЕ ПРИКОЛЮХИ*/}
            {isGenerating && isGenerating !== 'finished' && (
              <ProcessStatus currentProcess={currentProcess} />
            )}

            {/*ПРОЦЕСС ЗАКОНЧЕН - ДАЕМ ПОЛЬЗОВАТЕЛЮ СКАЧАТЬ РЕЗУЛЬТАТЫ*/}
            {isGenerating === 'finished' && (
              <Button onClick={downloadResults}>Скачать результаты</Button>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  )
}

export default AddUserPosition
